import { useState, FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d7ejrvbfimgqccpmabl0';

const interestOptions = [
  'Make a Donation',
  'Volunteer',
  'Corporate Partnership',
  'NGO / Organization Collaboration',
  'Fundraising',
  'Advocacy & Awareness',
  'Media & Press',
  'General Enquiry',
];

export default function GetInvolvedForm() {
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    organization: '',
    interest: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (form.message.length > 500) {
      setError('Message must be 500 characters or less.');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const body = new URLSearchParams({
        name: form.name,
        email: form.email,
        phone: form.phone,
        organization: form.organization,
        interest: form.interest,
        message: form.message,
      });
      await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="get-involved-form" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 items-start">
          {/* Left info panel */}
          <div className="lg:col-span-2">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-purple-200 bg-purple-50 mb-6">
              <span className="w-2 h-2 rounded-full bg-[#7B2D8B]"></span>
              <span className="text-[#7B2D8B] text-sm font-medium tracking-wide">Reach Out</span>
            </div>
            <h2 className="text-4xl font-extrabold text-gray-900 mb-5 leading-tight">
              Let&apos;s Connect &amp; <span className="text-[#7B2D8B]">Build Together</span>
            </h2>
            <p className="text-gray-500 text-base leading-relaxed mb-10">
              Whether you want to volunteer, partner, fundraise, or simply learn more — fill in the form and our team will get back to you within 48 hours.
            </p>

            <div className="space-y-5">
              {[
                { icon: 'ri-mail-line', label: 'Email', value: 'info@ikiruepilepsy.org' },
                { icon: 'ri-phone-line', label: 'Phone', value: '+234 809 955 4455' },
                { icon: 'ri-phone-line', label: 'Phone', value: '+234 706 090 8252' },
                { icon: 'ri-map-pin-line', label: 'Address', value: '235 Court Road, Kano, Nigeria' },
              ].map((c, i) => (
                <div key={i} className="flex items-start gap-4">
                  <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#7B2D8B]/10 shrink-0">
                    <i className={`${c.icon} text-[#7B2D8B] text-lg`}></i>
                  </div>
                  <div>
                    <div className="text-xs font-semibold uppercase tracking-widest text-gray-400 mb-0.5">{c.label}</div>
                    <div className="text-gray-800 text-sm font-medium">{c.value}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Purple Day callout */}
            <div className="mt-10 rounded-2xl bg-gradient-to-br from-[#2D0A35] to-[#7B2D8B] p-6 text-white">
              <div className="flex items-center gap-3 mb-3">
                <i className="ri-calendar-event-line text-[#C084FC] text-2xl"></i>
                <div className="font-bold text-lg">Purple Day — March 26</div>
              </div>
              <p className="text-purple-200 text-sm leading-relaxed">
                Join us every year on Purple Day to stand in solidarity with people living with epilepsy. Wear purple, share awareness, and help break the stigma.
              </p>
            </div>
          </div>

          {/* Right form */}
          <div className="lg:col-span-3 bg-[#FAF7FC] rounded-3xl p-8 md:p-10 border border-purple-100">
            {submitted ? (
              <div className="flex flex-col items-center justify-center py-20 text-center">
                <div className="w-20 h-20 flex items-center justify-center rounded-full bg-[#7B2D8B]/10 mb-6">
                  <i className="ri-checkbox-circle-line text-[#7B2D8B] text-4xl"></i>
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Thank You!</h3>
                <p className="text-gray-500 max-w-sm leading-relaxed mb-8">
                  We&apos;ve received your message and will be in touch within 48 hours. Together, we&apos;re making a difference!
                </p>
                <button
                  onClick={() => {
                    setSubmitted(false);
                    setForm({ name: '', email: '', phone: '', organization: '', interest: '', message: '' });
                  }}
                  className="px-6 py-3 rounded-full bg-[#7B2D8B] text-white text-sm font-semibold hover:bg-[#6a2578] transition-colors cursor-pointer whitespace-nowrap"
                >
                  Submit Another
                </button>
              </div>
            ) : (
              <>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Get Involved Form</h3>
                <p className="text-gray-500 text-sm mb-8">Tell us a bit about yourself and how you&apos;d like to support IKIRU.</p>

                <form data-readdy-form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 uppercase tracking-widest mb-2">Full Name *</label>
                      <input
                        type="text"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        required
                        placeholder="e.g. Fatima Bello"
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#7B2D8B] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 uppercase tracking-widest mb-2">Email Address *</label>
                      <input
                        type="email"
                        name="email"
                        value={form.email}
                        onChange={handleChange}
                        required
                        placeholder="you@example.com"
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#7B2D8B] transition-colors"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 uppercase tracking-widest mb-2">Phone Number</label>
                      <input
                        type="tel"
                        name="phone"
                        value={form.phone}
                        onChange={handleChange}
                        placeholder="+234 800 000 0000"
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#7B2D8B] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-700 uppercase tracking-widest mb-2">Organization / Company</label>
                      <input
                        type="text"
                        name="organization"
                        value={form.organization}
                        onChange={handleChange}
                        placeholder="Optional"
                        className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#7B2D8B] transition-colors"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-widest mb-2">I&apos;m Interested In *</label>
                    <select
                      name="interest"
                      value={form.interest}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 focus:outline-none focus:border-[#7B2D8B] transition-colors cursor-pointer"
                    >
                      <option value="">Select your area of interest...</option>
                      {interestOptions.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-widest mb-2">
                      Message *{' '}
                      <span className="text-gray-400 normal-case font-normal tracking-normal">
                        ({form.message.length}/500)
                      </span>
                    </label>
                    <textarea
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      required
                      maxLength={500}
                      rows={5}
                      placeholder="Tell us more about how you&apos;d like to get involved or support our mission..."
                      className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-white text-sm text-gray-800 placeholder-gray-400 focus:outline-none focus:border-[#7B2D8B] transition-colors resize-none"
                    />
                    {form.message.length > 500 && (
                      <p className="text-red-500 text-xs mt-1">Message cannot exceed 500 characters.</p>
                    )}
                  </div>

                  {error && (
                    <div className="flex items-center gap-2 text-red-500 text-sm">
                      <i className="ri-error-warning-line"></i>
                      <span>{error}</span>
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={submitting || form.message.length > 500}
                    className="w-full py-4 rounded-xl bg-[#7B2D8B] text-white font-semibold text-sm hover:bg-[#6a2578] transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center justify-center gap-2"
                  >
                    {submitting ? (
                      <><i className="ri-loader-4-line animate-spin"></i> Submitting...</>
                    ) : (
                      <><i className="ri-send-plane-line"></i> Submit &amp; Get Involved</>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
